import React, {useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';
import {Link, useParams} from 'react-router-dom';
import {observer} from 'mobx-react-lite';
import {Context} from '../../index';
import {fetchDataUnauth, fetchItemsPage} from '../../http/itemsAPI';
import ItemCard from './ItemCard';
import SpinnerDark from '../UI/SpinnerDark';
import Seo from '../seo/Seo';
import {CATEGORY_INFINITE_PAGE_SIZE, SHOP_ITEM_GRID_CLASS, SHOP_ROUTE, shopCategoryPath} from '../../util/Constants';
import {CATEGORIES} from '../../http/ApiEndpoints';
import {catalogItemId, hasCatalogItemId} from '../../util/catalogItemId';
import {useDebounced} from '../../hooks/useDebounced';
import {ShopFiltersSection, SHOP_SORT_OPTIONS} from './ShopFiltersSection';

const CategoryCatalog = observer(() => {
    const {categoryKey} = useParams();
    const {items: itemsStore} = useContext(Context);
    const categoryTitle = useMemo(() => {
        try {
            return decodeURIComponent(categoryKey || '');
        } catch {
            return categoryKey || '';
        }
    }, [categoryKey]);

    const [sort, setSort] = useState('title,asc');
    const [query, setQuery] = useState('');
    const [minPrice, setMinPrice] = useState('');
    const [maxPrice, setMaxPrice] = useState('');
    const [inStockOnly, setInStockOnly] = useState(true);
    const [filtersOpen, setFiltersOpen] = useState(false);
    const [sortOpen, setSortOpen] = useState(false);

    const debouncedQuery = useDebounced(query, 350);

    const [list, setList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [exhausted, setExhausted] = useState(false);
    const [error, setError] = useState('');

    const sentinelRef = useRef(null);
    const filterTokenRef = useRef(0);
    const pageRef = useRef(0);
    const firstFullPageLoadRef = useRef(true);
    const categoryLoadKeyRef = useRef(categoryTitle);

    useEffect(() => {
        if (itemsStore.categories.length === 0) {
            fetchDataUnauth(CATEGORIES).then((data) => itemsStore.setCategories(data));
        }
    }, [itemsStore]);

    const buildParams = useCallback(
        (pageIndex) => {
            const params = {
                page: pageIndex,
                size: CATEGORY_INFINITE_PAGE_SIZE,
                category: categoryTitle,
                sort,
                onlyAvailable: inStockOnly,
            };
            const q = debouncedQuery.trim();
            if (q) params.query = q;
            const min = minPrice === '' ? null : Number(minPrice);
            const max = maxPrice === '' ? null : Number(maxPrice);
            if (min != null && !Number.isNaN(min) && min >= 0) params.minPrice = min;
            if (max != null && !Number.isNaN(max) && max >= 0) params.maxPrice = max;
            return params;
        },
        [categoryTitle, sort, debouncedQuery, minPrice, maxPrice, inStockOnly],
    );

    useEffect(() => {
        if (!categoryTitle) return undefined;
        if (categoryLoadKeyRef.current !== categoryTitle) {
            firstFullPageLoadRef.current = true;
            categoryLoadKeyRef.current = categoryTitle;
        }

        filterTokenRef.current += 1;
        const token = filterTokenRef.current;
        pageRef.current = 0;
        setError('');

        if (firstFullPageLoadRef.current) {
            setList([]);
            setExhausted(false);
            setLoading(true);
        } else {
            setExhausted(true);
        }

        fetchItemsPage(buildParams(0))
            .then((data) => {
                if (token !== filterTokenRef.current) return;
                const chunk = data.items || [];
                setList(chunk);
                setExhausted(Boolean(data.last) || chunk.length === 0);
            })
            .catch(() => {
                if (token !== filterTokenRef.current) return;
                setError('Не удалось загрузить товары.');
                setList([]);
                setExhausted(true);
            })
            .finally(() => {
                if (token !== filterTokenRef.current) return;
                setLoading(false);
                firstFullPageLoadRef.current = false;
            });
    }, [categoryTitle, buildParams]);

    const loadNextPage = useCallback(() => {
        if (!categoryTitle || exhausted || loadingMore || loading) return;
        const token = filterTokenRef.current;
        const next = pageRef.current + 1;
        setLoadingMore(true);
        fetchItemsPage(buildParams(next))
            .then((data) => {
                if (token !== filterTokenRef.current) return;
                const chunk = data.items || [];
                if (chunk.length > 0) {
                    setList((prev) => [...prev, ...chunk]);
                    pageRef.current = next;
                }
                setExhausted(Boolean(data.last) || chunk.length === 0);
            })
            .catch(() => {
                if (token !== filterTokenRef.current) return;
                setExhausted(true);
            })
            .finally(() => {
                if (token !== filterTokenRef.current) return;
                setLoadingMore(false);
            });
    }, [categoryTitle, exhausted, loadingMore, loading, buildParams]);

    useEffect(() => {
        const el = sentinelRef.current;
        if (!el || exhausted || loading) return undefined;
        const obs = new IntersectionObserver(
            (entries) => {
                if (entries[0]?.isIntersecting) {
                    loadNextPage();
                }
            },
            {root: null, rootMargin: '160px', threshold: 0},
        );
        obs.observe(el);
        return () => obs.disconnect();
    }, [loadNextPage, exhausted, loading, list.length]);

    if (!categoryTitle) {
        return (
            <div className="lut-card">
                <p className="text-bark-600">Категория не указана.</p>
                <Link to={SHOP_ROUTE} className="mt-4 inline-block font-medium text-pine-700 hover:underline">
                    В каталог
                </Link>
            </div>
        );
    }

    return (
        <div className="pb-8">
            <Seo
                title={categoryTitle}
                description={`${categoryTitle} — ароматические свечи ручной работы Lutica: натуральные составы для дома, онлайн-заказ и доставка по России.`}
                pathname={shopCategoryPath(categoryTitle)}
            />
            <div className="mb-3">
                <Link
                    to={SHOP_ROUTE}
                    className="text-sm font-medium text-pine-700 hover:underline touch-manipulation"
                >
                    ← Весь каталог
                </Link>
            </div>
            <ShopFiltersSection
                leading={
                    <h1 className="text-xl font-semibold tracking-tight text-bark-900 sm:text-2xl">
                        {categoryTitle}
                    </h1>
                }
                sort={sort}
                setSort={setSort}
                sortOptions={SHOP_SORT_OPTIONS}
                filtersOpen={filtersOpen}
                setFiltersOpen={setFiltersOpen}
                sortOpen={sortOpen}
                setSortOpen={setSortOpen}
                query={query}
                setQuery={setQuery}
                minPrice={minPrice}
                setMinPrice={setMinPrice}
                maxPrice={maxPrice}
                setMaxPrice={setMaxPrice}
                inStockOnly={inStockOnly}
                setInStockOnly={setInStockOnly}
                idPrefix="category"
            />

            {loading ? (
                <div className="mt-10">
                    <SpinnerDark/>
                </div>
            ) : error ? (
                <div className="lut-card mt-8 text-red-800">{error}</div>
            ) : list.length === 0 ? (
                <p className="mt-10 text-bark-600">В этой категории пока нет товаров по заданным условиям.</p>
            ) : (
                <>
                    <div className={`mt-8 ${SHOP_ITEM_GRID_CLASS}`}>
                        {list.filter(hasCatalogItemId).map((item) => (
                            <ItemCard key={catalogItemId(item)} item={item}/>
                        ))}
                    </div>
                    {!exhausted && (
                        <div ref={sentinelRef} className="flex min-h-14 items-center justify-center py-4">
                            {loadingMore ? (
                                <div className="h-8 w-8 animate-spin rounded-full border-2 border-pine-200 border-t-pine-700"/>
                            ) : null}
                        </div>
                    )}
                </>
            )}

            <div className="mt-12 border-t border-stone-200/70 pt-8">
                <p className="text-sm font-medium text-bark-600">Другие категории</p>
                <div className="mt-3 flex flex-wrap gap-2">
                    {itemsStore.categories
                        .filter((c) => c.title !== categoryTitle)
                        .map((c) => (
                            <Link
                                key={c.title}
                                to={shopCategoryPath(c.title)}
                                className="rounded-full border border-stone-200 bg-white px-3 py-1.5 text-sm text-bark-700 hover:border-pine-300 hover:bg-cream-50"
                            >
                                {c.title}
                            </Link>
                        ))}
                </div>
            </div>
        </div>
    );
});

export default CategoryCatalog;
